import GeoJSONFormat from 'ol/format/GeoJSON';
import type SimpleGeometry from 'ol/geom/SimpleGeometry';
import { getArea, getLength } from 'ol/sphere';
import type { SelectedFeatureMetrics } from '../map/MapContext';

const geoJsonFormat = new GeoJSONFormat();

/** Same shape/math as MapContext's computeFeatureMetrics, but starting from a
 * GeoJSON geometry (EPSG:4326, straight from the API) instead of an OL
 * feature already on the map — the object card has no map to read from. */
export function computeGeometryMetrics(geometry: { type: string; coordinates?: unknown }): SelectedFeatureMetrics {
  const geom = geoJsonFormat.readGeometry(geometry) as SimpleGeometry;
  const type = geom.getType();
  const opts = { projection: 'EPSG:4326' };

  if (type === 'Point') {
    const [lon, lat] = (geom as unknown as { getCoordinates: () => number[] }).getCoordinates();
    return { kind: 'point', lon, lat };
  }
  if (type === 'MultiPoint') {
    const coords = (geom as unknown as { getCoordinates: () => number[][] }).getCoordinates();
    if (coords.length === 0) return null;
    const [lon, lat] = coords[0];
    return { kind: 'point', lon, lat };
  }
  if (type === 'LineString' || type === 'MultiLineString') {
    const lengthM = getLength(geom, opts);
    const coords: number[][] =
      type === 'LineString'
        ? (geom as unknown as { getCoordinates: () => number[][] }).getCoordinates()
        : (geom as unknown as { getCoordinates: () => number[][][] }).getCoordinates().flat();
    if (coords.length < 2) return null;
    return {
      kind: 'line',
      lengthM,
      start: coords[0] as [number, number],
      end: coords[coords.length - 1] as [number, number],
    };
  }
  if (type === 'Polygon' || type === 'MultiPolygon') {
    const areaM2 = getArea(geom, opts);
    const perimeterM = getLength(geom, opts);
    const ring: number[][] =
      type === 'Polygon'
        ? (geom as unknown as { getCoordinates: () => number[][][] }).getCoordinates()[0]
        : (geom as unknown as { getCoordinates: () => number[][][][] }).getCoordinates()[0][0];
    const vertices = ring.slice(0, -1).map((c) => c as [number, number]);
    return { kind: 'polygon', areaM2, perimeterM, vertices };
  }
  return null;
}

/** A single [lon, lat] "somewhere on this geometry" — the point itself, a
 * line's start, or a polygon's first vertex. Used as the initial (throwaway)
 * position when creating a brand-new child object that's about to be snapped
 * onto its parent anyway (see ObjectCardBody's "Создать новый" child flow) —
 * create_feature needs *some* valid point to create the object with before
 * the follow-up snap_to_parent call moves it to the precise canonical spot. */
export function pickRepresentativePoint(geometry: { type: string; coordinates?: unknown }): [number, number] | null {
  const metrics = computeGeometryMetrics(geometry);
  if (!metrics) return null;
  if (metrics.kind === 'point') return [metrics.lon, metrics.lat];
  if (metrics.kind === 'line') return metrics.start;
  return metrics.vertices[0] ?? null;
}

export function formatLength(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} км` : `${m.toFixed(0)} м`;
}

export function formatArea(m2: number): string {
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} га`;
  if (m2 >= 100) return `${(m2 / 100).toFixed(2)} сот.`;
  return `${m2.toFixed(1)} м²`;
}

export function formatLonLat([lon, lat]: [number, number]): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}
